import React, { useEffect, useState } from 'react';
import { usePromotionsStore } from '../../../../store/promotionsStore';
import { Promotion, DiscountType } from '../../../../services/promotionsApi';
import styles from './EditPromotionModal.module.css';

interface EditPromotionModalProps {
  open: boolean;
  promotion: Promotion | null;
  onClose: () => void;
}

const toDateInputValue = (iso: string) => {
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return ''; }
};

export const EditPromotionModal: React.FC<EditPromotionModalProps> = ({ open, promotion, onClose }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [discountType, setDiscountType] = useState<DiscountType>('percent');
  const [discountValue, setDiscountValue] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const update = usePromotionsStore((s) => s.update);

  useEffect(() => {
    if (open && promotion) {
      setName(promotion.name);
      setDescription(promotion.description || '');
      setDiscountType(promotion.discount_type);
      setDiscountValue(String(promotion.discount_value));
      setStartsAt(toDateInputValue(promotion.starts_at));
      setEndsAt(toDateInputValue(promotion.ends_at));
      setIsActive(promotion.is_active);
      setError(null);
      setSubmitting(false);
    }
  }, [open, promotion]);

  if (!open || !promotion) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required'); return; }
    const val = parseFloat(discountValue);
    if (isNaN(val) || val < 0) { setError('Discount must be a positive number'); return; }
    if (!startsAt || !endsAt) { setError('Start and end dates are required'); return; }
    if (new Date(startsAt) > new Date(endsAt)) { setError('Start date must be before end date'); return; }
    setError(null);
    setSubmitting(true);
    try {
      await update(promotion.id, {
        name: name.trim(),
        description: description.trim() || undefined,
        discount_type: discountType,
        discount_value: val,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
        is_active: isActive,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update promotion');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2 className={styles.title}>Edit Promotion</h2>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <p className={styles.error}>{error}</p>}

          <label className={styles.field}>
            <span>Name <span className={styles.required}>*</span></span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required autoFocus maxLength={120} />
          </label>

          <label className={styles.field}>
            <span>Description</span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={500} />
          </label>

          <div className={styles.row}>
            <label className={styles.field}>
              <span>Discount Type</span>
              <select value={discountType} onChange={(e) => setDiscountType(e.target.value as DiscountType)}>
                <option value="percent">Percent (%)</option>
                <option value="flat">Flat ($)</option>
              </select>
            </label>
            <label className={styles.field}>
              <span>Discount Value <span className={styles.required}>*</span></span>
              <input type="number" min="0" step="0.01" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} required />
            </label>
          </div>

          <div className={styles.row}>
            <label className={styles.field}>
              <span>Starts At <span className={styles.required}>*</span></span>
              <input type="date" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} required />
            </label>
            <label className={styles.field}>
              <span>Ends At <span className={styles.required}>*</span></span>
              <input type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} required />
            </label>
          </div>

          <label className={styles.toggle}>
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <span>Active</span>
          </label>

          <div className={styles.actions}>
            <button type="button" className={styles.cancel} onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className={styles.submit} disabled={submitting}>
              {submitting ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPromotionModal;
